import React, { useState, useEffect, useCallback, useMemo } from "react";
import api, { fmtVND, fmtFull } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  FileSpreadsheet, Upload, Camera, Loader2, Plus, Trash2, AlertTriangle,
  TrendingUp, TrendingDown, DollarSign, Users, BarChart3, Eye,
  CheckCircle2, Download, Calendar, Filter, Brain, Wallet, List, BookOpen, Banknote, LayoutList, GitCompare,
  Sparkles, ScanLine, Image as ImageIcon, FileDown, Eraser,
} from "lucide-react";
import { DateRangePicker } from "@/components/DateRangePicker";
import SalaryTab from "@/components/accounting/SalaryTab";
import AlertsTab from "@/components/accounting/AlertsTab";
import SoThuChiTab from "@/components/accounting/SoThuChiTab";
import ImportUploadTab from "@/components/accounting/ImportUploadTab";
import BaoCaoKDTab from "@/components/accounting/BaoCaoKDTab";
import DoanhThuPippyTab from "@/components/accounting/DoanhThuPippyTab";
import DoanhThuNgayTab from "@/components/accounting/DoanhThuNgayTab";
import ReconcileKVTab from "@/components/accounting/ReconcileKVTab";
import QuickDateButtons from "@/components/accounting/QuickDateButtons";
import ApexChart from "react-apexcharts";

const CAT_LABELS = {
  nguyen_lieu: "Nguyên liệu", van_hanh: "Vận hành", luong: "Lương",
  marketing: "Marketing", sua_chua: "Sửa chữa", thue_phi: "Thuế/Phí",
  khac: "Khác", doanh_thu: "Doanh thu",
};
const PM_LABELS = {
  tien_mat: "Tiền mặt", vnpay: "VNPay", momo: "Momo",
  grab: "Grab", quet_the: "Quét thẻ", chuyen_khoan: "Chuyển khoản", khac: "Khác",
};

const SUB_TABS = [
  { key: "dashboard", label: "Tổng quan", icon: BarChart3 },
  { key: "bao-cao-kd", label: "Báo cáo KD", icon: TrendingUp },
  { key: "so-thu-chi", label: "Sổ Thu Chi", icon: BookOpen },
  { key: "bang-ke-tien-mat", label: "Bảng Kê Tiền Mặt", icon: Banknote },
  { key: "bang-ke-doanh-thu", label: "Doanh thu Pippy", icon: LayoutList },
  { key: "doanh-thu-ngay", label: "Doanh thu ngày", icon: BarChart3 },
  { key: "reconcile-kv", label: "Đối chiếu KV", icon: GitCompare },
  { key: "import", label: "Nhập liệu", icon: Download },
  { key: "salary", label: "Quản lý Lương", icon: Users },
  { key: "alerts", label: "Cảnh báo", icon: AlertTriangle },
];

export default function AccountingPage() {
  const [subTab, setSubTab] = useState("dashboard");
  const [wipeOpen, setWipeOpen] = useState(false);
  const [wipePin, setWipePin] = useState("");
  const [wiping, setWiping] = useState(false);
  const [wipeResult, setWipeResult] = useState(null);

  const handleWipeAll = async () => {
    if (!wipePin) {
      toast.error("Vui lòng nhập PIN");
      return;
    }
    setWiping(true);
    setWipeResult(null);
    try {
      const { data } = await api.post("/accounting/wipe-all", { pin: wipePin });
      setWipeResult(data);
      setWipePin("");
      toast.success(data.message || "Đã xoá toàn bộ dữ liệu kế toán");
    } catch (err) {
      const detail = err.response?.data?.detail || "Lỗi xoá";
      toast.error(detail);
      if (err.response?.status === 403) {
        // PIN sai — không đóng dialog, để user thử lại
      }
    } finally {
      setWiping(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="accounting-page">
      <div className="flex items-center gap-3 justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#4680ff] to-[#6366f1] flex items-center justify-center shadow-lg">
            <FileSpreadsheet className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-[#F8FAFC]">Báo cáo Kế toán</h1>
            <p className="text-[10px] text-slate-500">AI OCR · Thu Chi · Lương · Biểu đồ</p>
          </div>
        </div>
        <Button
          onClick={() => { setWipeOpen(true); setWipeResult(null); setWipePin(""); }}
          variant="outline"
          size="sm"
          className="border-red-500/40 text-red-400 hover:bg-red-500/10 h-8 text-xs"
          data-testid="acct-wipe-all-btn"
          title="Xoá toàn bộ dữ liệu kế toán đã import (cần PIN)"
        >
          <Eraser className="w-3.5 h-3.5 mr-1" /> Xoá tất cả
        </Button>
      </div>

      <div className="flex gap-1 overflow-x-auto pb-px">
        {SUB_TABS.map(t => (
          <button key={t.key} onClick={() => setSubTab(t.key)} data-testid={`acct-tab-${t.key}`}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${subTab === t.key ? "bg-[#1e293b] text-lime-400 shadow" : "text-slate-500 hover:text-slate-300"}`}>
            <t.icon className="w-3.5 h-3.5" /> {t.label}
          </button>
        ))}
      </div>

      {subTab === "dashboard" && <DashboardTab />}
      {subTab === "bao-cao-kd" && <BaoCaoKDTab />}
      {subTab === "so-thu-chi" && <SoThuChiTab />}
      {subTab === "bang-ke-tien-mat" && <BangKeTienMatTab />}
      {subTab === "bang-ke-doanh-thu" && <DoanhThuPippyTab />}
      {subTab === "doanh-thu-ngay" && <DoanhThuNgayTab />}
      {subTab === "reconcile-kv" && <ReconcileKVTab />}
      {subTab === "import" && <ImportUploadTab />}
      {subTab === "salary" && <SalaryTab />}
      {subTab === "alerts" && <AlertsTab />}

      {/* Wipe All Dialog */}
      <Dialog open={wipeOpen} onOpenChange={setWipeOpen}>
        <DialogContent className="bg-[#1e293b] border-red-500/40 sm:max-w-md" data-testid="acct-wipe-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-400 text-base flex items-center gap-2">
              <Eraser className="w-4 h-4" />
              Xoá toàn bộ dữ liệu Kế toán
            </DialogTitle>
          </DialogHeader>
          {!wipeResult ? (
            <div className="space-y-3">
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-[11px] text-red-300 space-y-1">
                <div className="font-semibold flex items-center gap-1">
                  <AlertTriangle className="w-3.5 h-3.5" /> Sẽ xoá vĩnh viễn (KHÔNG thể hoàn tác):
                </div>
                <ul className="list-disc pl-4 space-y-0.5 text-[10.5px]">
                  <li>Sổ Thu Chi · Bảng Kê Tiền Mặt · Báo cáo KD · Chứng từ</li>
                  <li>Doanh thu Pippy (entries + tổng KiotViet + tổng sổ kết vòng)</li>
                  <li>Doanh thu ngày (toàn bộ entries)</li>
                  <li>Sổ quỹ (cashbook) · Lịch sử file upload</li>
                  <li>Lịch sử Discord auto-import + Cache OCR</li>
                </ul>
              </div>
              <div>
                <label className="text-[11px] text-slate-400 mb-1 block font-semibold uppercase">
                  Nhập PIN xác nhận (6 số)
                </label>
                <Input
                  type="password"
                  inputMode="numeric"
                  maxLength={6}
                  value={wipePin}
                  onChange={(e) => setWipePin(e.target.value.replace(/\D/g, ""))}
                  placeholder="••••••"
                  className="h-10 bg-[#0f172a] border-red-500/30 text-red-300 text-base text-center font-mono tracking-[0.5em] focus-visible:border-red-400"
                  data-testid="acct-wipe-pin-input"
                  autoFocus
                />
                <p className="text-[10px] text-slate-500 mt-1 italic">
                  Hỏi anh/chị quản lý hoặc xem trong tài liệu hệ thống.
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3 text-[12px] text-emerald-300">
                <div className="font-semibold flex items-center gap-1.5 mb-1">
                  <CheckCircle2 className="w-4 h-4" /> Đã xoá thành công
                </div>
                <p className="text-[11px]">Tổng <strong>{wipeResult.total_deleted}</strong> bản ghi đã được xoá.</p>
              </div>
              <details className="text-[10.5px]">
                <summary className="cursor-pointer text-slate-400 hover:text-slate-200">Chi tiết theo collection</summary>
                <div className="mt-2 space-y-0.5 max-h-[200px] overflow-y-auto pr-1">
                  {Object.entries(wipeResult.deleted || {}).map(([k, v]) => (
                    <div key={k} className="flex justify-between text-slate-400 font-mono">
                      <span className="truncate">{k}</span>
                      <span className={v > 0 ? "text-amber-300" : "text-slate-600"}>{v}</span>
                    </div>
                  ))}
                </div>
              </details>
            </div>
          )}
          <DialogFooter className="gap-2">
            {!wipeResult ? (
              <>
                <Button
                  variant="outline"
                  onClick={() => { setWipeOpen(false); setWipePin(""); }}
                  className="border-[#334155] text-slate-300 hover:bg-[#334155] h-8 text-xs"
                >
                  Huỷ
                </Button>
                <Button
                  onClick={handleWipeAll}
                  disabled={wiping || wipePin.length < 4}
                  className="bg-red-500 hover:bg-red-400 text-white h-8 text-xs disabled:opacity-50"
                  data-testid="acct-wipe-confirm-btn"
                >
                  {wiping ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Đang xoá...</> : <><Eraser className="w-3.5 h-3.5 mr-1" /> Xác nhận xoá</>}
                </Button>
              </>
            ) : (
              <Button
                onClick={() => { setWipeOpen(false); setWipeResult(null); window.location.reload(); }}
                className="bg-emerald-500 hover:bg-emerald-400 text-white h-8 text-xs"
                data-testid="acct-wipe-done-btn"
              >
                Đóng & tải lại
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}



// ==================== DASHBOARD ====================
function DashboardTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [start, setStart] = useState(() => new Date().toISOString().slice(0, 10));
  const [end, setEnd] = useState(() => new Date().toISOString().slice(0, 10));
  const [groupBy, setGroupBy] = useState("day");

  // Salary data for charts
  const [salaryRecords, setSalaryRecords] = useState([]);
  const [salarySummary, setSalarySummary] = useState([]);

  const fetch = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get(`/accounting/dashboard?start=${start}&end=${end}&group_by=${groupBy}`); setData(r.data); } catch {}
    finally { setLoading(false); }
  }, [start, end, groupBy]);
  useEffect(() => { fetch(); }, [fetch]);

  // Load salary data once
  useEffect(() => {
    api.get("/accounting/salary-summary").then(({ data: s }) => {
      setSalarySummary(s.months || []);
      const latestM = s.latest_month || "";
      if (latestM) {
        api.get(`/accounting/salary?month=${latestM}`).then(({ data: sr }) => {
          setSalaryRecords(sr.records || []);
        }).catch(() => {});
      }
    }).catch(() => {});
  }, []);

  // Salary deduped + dept breakdown
  const salaryDeduped = useMemo(() => {
    const map = new Map();
    for (const r of salaryRecords) {
      const code = r.ma_nv || r.employee_code || "";
      if (!code) continue;
      const dept = (r.department || "").toUpperCase();
      if (!dept.includes("BẢNG LƯƠNG") && !dept.includes("BANG LUONG")) continue;
      const key = `${code}_${r.month}`;
      const existing = map.get(key);
      if (!existing) { map.set(key, r); }
      else {
        const scoreNew = (r.muc_luong || 0) + (r.tong_luong || 0) + (r.so_cong ? 1000 : 0);
        const scoreOld = (existing.muc_luong || 0) + (existing.tong_luong || 0) + (existing.so_cong ? 1000 : 0);
        if (scoreNew > scoreOld) map.set(key, r);
      }
    }
    return Array.from(map.values());
  }, [salaryRecords]);

  const salaryDeptBreakdown = useMemo(() => {
    const map = {};
    for (const r of salaryDeduped) {
      const d = (r.department || "Khac").replace("BẢNG LƯƠNG ", "").replace("BẢNG LƯƠNG- ", "").trim();
      if (!map[d]) map[d] = { name: d, count: 0, totalSalary: 0 };
      map[d].count += 1;
      map[d].totalSalary += r.tong_luong || r.muc_luong || 0;
    }
    return Object.values(map).sort((a, b) => b.totalSalary - a.totalSalary);
  }, [salaryDeduped]);

  const DONUT_COLORS = ["#4680ff", "#05c3a6", "#a78bfa", "#fb923c", "#fc5286", "#fbbf24", "#38bdf8", "#22c55e"];

  function shortName(fullName) {
    if (!fullName) return "";
    const parts = fullName.trim().split(/\s+/);
    if (parts.length <= 2) return fullName;
    return `${parts[0]} ${parts[parts.length - 1]}`;
  }

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 text-lime-400 animate-spin" /></div>;
  if (!data) return null;

  return (
    <div className="space-y-4" data-testid="acct-dashboard">
      {/* Filters */}
      <div className="flex gap-2 flex-wrap items-center">
        <DateRangePicker start={start} end={end} onChangeStart={setStart} onChangeEnd={setEnd} />
        <QuickDateButtons setStart={setStart} setEnd={setEnd} />
        {["day", "week", "month"].map(g => (
          <button key={g} onClick={() => setGroupBy(g)} className={`px-2.5 py-1 rounded text-[10px] ${groupBy === g ? "bg-lime-500/20 text-lime-400" : "text-slate-500"}`}>
            {g === "day" ? "Ngày" : g === "week" ? "Tuần" : "Tháng"}
          </button>
        ))}
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: "TONG THU", value: data.total_thu, gradient: "from-[#4680ff] to-[#5b8def]", spark: data.chart_data?.map(d => d.thu) || [] },
          { label: "TONG CHI", value: data.total_chi, gradient: "from-[#fc5286] to-[#fd6d8e]", spark: data.chart_data?.map(d => d.chi) || [] },
          { label: "LOI NHUAN", value: data.balance, gradient: data.balance >= 0 ? "from-[#05c3a6] to-[#22d6b4]" : "from-[#e74c3c] to-[#ef4444]", spark: data.chart_data?.map(d => d.profit) || [] },
          { label: "GIAO DICH", value: data.transaction_count, gradient: "from-[#fd7e14] to-[#ff9a44]", spark: [] },
        ].map((m, i) => (
          <div key={i} className={`relative overflow-hidden rounded-xl bg-gradient-to-b ${m.gradient} p-4 min-h-[130px] flex flex-col justify-between shadow-lg`}>
            <p className="text-[10px] font-bold text-white/80 tracking-wider uppercase">{m.label}</p>
            <p className="text-2xl font-bold text-white">{typeof m.value === "number" && i < 3 ? fmtVND(m.value) : m.value}</p>
            <p className="text-[10px] text-white/60">{data.period}</p>
            {m.spark.length > 0 && (
              <div className="absolute bottom-0 left-0 right-0 opacity-35">
                <ApexChart type="area" height={45} width="100%" options={{ chart: { sparkline: { enabled: true }, animations: { enabled: true, speed: 1000 } }, stroke: { curve: 'smooth', width: 2 }, fill: { type: 'gradient', gradient: { opacityFrom: 0.5, opacityTo: 0 } }, colors: ['#fff'], tooltip: { enabled: false } }} series={[{ data: m.spark }]} />
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Main Chart: Thu vs Chi */}
      {data.chart_data?.length > 0 && (
        <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-4">
          <p className="text-xs text-slate-400 mb-2">Thu vs Chi theo {groupBy === "day" ? "ngày" : groupBy === "week" ? "tuần" : "tháng"}</p>
          <ApexChart type="bar" height={220} options={{
            chart: { toolbar: { show: false }, animations: { enabled: true, speed: 600 } },
            plotOptions: { bar: { borderRadius: 4, columnWidth: '55%' } },
            colors: ['#4680ff', '#fc5286', '#05c3a6'],
            xaxis: { categories: data.chart_data.map(d => d.period.length > 7 ? d.period.slice(5) : d.period), labels: { style: { fontSize: '9px' } } },
            yaxis: { labels: { formatter: v => fmtVND(v), style: { fontSize: '9px' } } },
            grid: { borderColor: '#334155', strokeDashArray: 3 },
            legend: { position: 'bottom', fontSize: '10px' },
            tooltip: { y: { formatter: v => fmtFull(v) } },
            dataLabels: { enabled: false },
          }} series={[
            { name: 'Thu', data: data.chart_data.map(d => d.thu) },
            { name: 'Chi', data: data.chart_data.map(d => d.chi) },
            { name: 'Lợi nhuận', data: data.chart_data.map(d => d.profit) },
          ]} />
        </div>
      )}

      {/* KiotViet vs OCR Comparison */}
      <KVComparisonSection start={start} end={end} />

      {/* ═══ SALARY CHARTS (moved from Quản lý Lương) ═══ */}
      {salaryDeduped.length > 0 && salaryDeptBreakdown.length > 0 && (
        <>
          <h3 className="text-xs font-bold text-slate-300 uppercase tracking-wider mt-4">Quỹ lương</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {/* Pie: Tổng lương bộ phận */}
            {(
              <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-4" data-testid="dashboard-salary-pie">
                <p className="text-xs text-slate-400 mb-2">Tổng lương theo bộ phận</p>
                <ApexChart type="donut" height={240}
                  options={{
                    chart: { animations: { enabled: true, speed: 600 } },
                    labels: salaryDeptBreakdown.map(d => d.name),
                    colors: DONUT_COLORS.slice(0, salaryDeptBreakdown.length),
                    legend: { position: 'bottom', fontSize: '9px', labels: { colors: '#94a3b8' },
                      formatter: (name, opts) => `${name}: ${fmtVND(opts.w.config.series[opts.seriesIndex])}`
                    },
                    plotOptions: { pie: { donut: { size: '55%', labels: { show: true,
                      total: { show: true, label: 'Tổng', fontSize: '10px', color: '#94a3b8',
                        formatter: (w) => fmtVND(w.globals.seriesTotals.reduce((a, b) => a + b, 0))
                      },
                      value: { fontSize: '13px', color: '#f8fafc', fontWeight: 700, formatter: v => fmtVND(Number(v)) },
                      name: { fontSize: '9px', color: '#94a3b8' },
                    } } } },
                    dataLabels: { enabled: false },
                    stroke: { show: false },
                    tooltip: { theme: 'dark', y: { formatter: v => fmtFull(v) } },
                  }}
                  series={salaryDeptBreakdown.map(d => d.totalSalary)}
                />
              </div>
            )}
            {/* Treemap */}
            {(
              <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-4" data-testid="dashboard-salary-treemap">
                <p className="text-xs text-slate-400 mb-2">Mức nhận lương nhân viên</p>
                <ApexChart type="treemap" height={240}
                  options={{
                    chart: { toolbar: { show: false }, animations: { enabled: true, speed: 600 } },
                    colors: ['#4680ff', '#38bdf8', '#06b6d4', '#05c3a6', '#22c55e', '#84cc16'],
                    plotOptions: { treemap: { distributed: true, enableShades: true, shadeIntensity: 0.5 } },
                    dataLabels: { enabled: true, style: { fontSize: '10px', fontWeight: 600 }, formatter: (text, op) => [text, fmtVND(op.value)], offsetY: -4 },
                    tooltip: { theme: 'dark', y: { formatter: v => fmtFull(v) } },
                  }}
                  series={[{ data: salaryDeduped.filter(r => (r.thuc_nhan || r.net_salary || 0) > 0).map(r => ({ x: shortName(r.ten || r.employee_name || ""), y: r.thuc_nhan || r.net_salary || 0 })).sort((a, b) => b.y - a.y).slice(0, 25) }]}
                />
              </div>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {/* Donut: NV bộ phận */}
            {(
              <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-4" data-testid="dashboard-salary-donut">
                <p className="text-xs text-slate-400 mb-2">Nhân sự theo bộ phận</p>
                <ApexChart type="donut" height={220}
                  options={{
                    chart: { animations: { enabled: true, speed: 600 } },
                    labels: salaryDeptBreakdown.map(d => d.name),
                    colors: DONUT_COLORS.slice(0, salaryDeptBreakdown.length),
                    legend: { position: 'bottom', fontSize: '9px', labels: { colors: '#94a3b8' } },
                    plotOptions: { pie: { donut: { size: '60%', labels: { show: true, total: { show: true, label: 'Tổng NV', fontSize: '10px', color: '#94a3b8', formatter: () => `${salaryDeduped.length}` }, value: { fontSize: '15px', color: '#f8fafc', fontWeight: 700 }, name: { fontSize: '9px', color: '#94a3b8' } } } } },
                    dataLabels: { enabled: false },
                    stroke: { show: false },
                    tooltip: { theme: 'dark', y: { formatter: v => `${v} nhân viên` } },
                  }}
                  series={salaryDeptBreakdown.map(d => d.count)}
                />
              </div>
            )}
            {/* Radar */}
            {(
              <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-4" data-testid="dashboard-salary-radar">
                <p className="text-xs text-slate-400 mb-2">Radar lương bộ phận</p>
                <ApexChart type="radar" height={220}
                  options={{
                    chart: { toolbar: { show: false }, animations: { enabled: true, speed: 600 } },
                    xaxis: { categories: salaryDeptBreakdown.map(d => d.name.length > 12 ? d.name.slice(0, 12) + "..." : d.name) },
                    yaxis: { show: false },
                    colors: ['#4680ff'],
                    stroke: { width: 2 },
                    fill: { opacity: 0.25 },
                    markers: { size: 3 },
                    tooltip: { theme: 'dark', y: { formatter: v => fmtFull(v) } },
                    plotOptions: { radar: { polygons: { strokeColors: '#334155', connectorColors: '#334155', fill: { colors: ['#0f172a', '#1e293b'] } } } },
                  }}
                  series={[{ name: 'Lương', data: salaryDeptBreakdown.map(d => d.totalSalary) }]}
                />
              </div>
            )}
          </div>
        </>
      )}

      {/* Bottom row: Payment methods + Areas + Expense categories */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {data.payment_methods?.length > 0 && (
          <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-4">
            <p className="text-xs text-slate-400 mb-2">Phương thức thanh toán</p>
            <ApexChart type="donut" height={180} options={{
              labels: data.payment_methods.map(p => PM_LABELS[p.method] || p.method),
              colors: ['#4680ff', '#05c3a6', '#fc5286', '#fd7e14', '#7c3aed', '#0891b2'],
              legend: { position: 'bottom', fontSize: '9px', labels: { colors: '#94a3b8' } },
              tooltip: { y: { formatter: v => fmtFull(v) } },
              dataLabels: { enabled: false },
            }} series={data.payment_methods.map(p => p.amount)} />
          </div>
        )}

        {data.areas?.length > 0 && (
          <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-4">
            <p className="text-xs text-slate-400 mb-2">Doanh thu theo khu</p>
            <ApexChart type="donut" height={180} options={{
              labels: data.areas.map(a => a.area),
              colors: ['#22c03c', '#4680ff', '#fc5286', '#fd7e14', '#7c3aed'],
              legend: { position: 'bottom', fontSize: '9px', labels: { colors: '#94a3b8' } },
              tooltip: { y: { formatter: v => fmtFull(v) } },
              dataLabels: { enabled: false },
            }} series={data.areas.map(a => a.amount)} />
          </div>
        )}

        {data.expense_categories?.length > 0 && (
          <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-4">
            <p className="text-xs text-slate-400 mb-2">Chi phí theo loại</p>
            <ApexChart type="donut" height={180} options={{
              labels: data.expense_categories.map(c => CAT_LABELS[c.category] || c.category),
              colors: ['#fc5286', '#fd7e14', '#7c3aed', '#4680ff', '#05c3a6', '#0891b2', '#94a3b8'],
              legend: { position: 'bottom', fontSize: '9px', labels: { colors: '#94a3b8' } },
              tooltip: { y: { formatter: v => fmtFull(v) } },
              dataLabels: { enabled: false },
            }} series={data.expense_categories.map(c => c.amount)} />
          </div>
        )}
      </div>
    </div>
  );
}


// ==================== BẢNG KÊ TIỀN MẶT ====================
function BangKeTienMatTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [start, setStart] = useState(() => {
    const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-01`;
  });
  const [end, setEnd] = useState(() => new Date().toISOString().slice(0, 10));
  const [view, setView] = useState("detail");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get(`/accounting/bang-ke-tien-mat?start=${start}&end=${end}&view=${view}`); setData(r.data); } catch {}
    finally { setLoading(false); }
  }, [start, end, view]);
  useEffect(() => { fetchData(); }, [fetchData]);

  const MENH_GIA_COLORS = {
    500000: "#ef4444", 200000: "#f97316", 100000: "#eab308",
    50000: "#22c55e", 20000: "#06b6d4", 10000: "#3b82f6",
    5000: "#8b5cf6", 2000: "#ec4899", 1000: "#64748b",
  };

  // Aggregate denominations across all records for charts
  const aggDenominations = (data?.records || []).reduce((acc, rec) => {
    (rec.denominations || []).forEach(d => {
      const key = d.menh_gia;
      if (!acc[key]) acc[key] = { menh_gia: key, so_to: 0, thanh_tien: 0 };
      acc[key].so_to += d.so_to;
      acc[key].thanh_tien += d.thanh_tien;
    });
    return acc;
  }, {});
  const aggList = Object.values(aggDenominations).sort((a, b) => b.menh_gia - a.menh_gia);
  const grandTotal = aggList.reduce((a, d) => a + d.thanh_tien, 0);

  return (
    <div className="space-y-4" data-testid="acct-bang-ke-tien-mat">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-sm font-bold text-slate-200 flex items-center gap-2">
          <Banknote className="w-4 h-4 text-amber-400" /> Bảng Kê Tiền Mặt
        </h2>
        <div className="flex items-center gap-2">
          <DateRangePicker start={start} end={end} onChangeStart={setStart} onChangeEnd={setEnd} />
          <QuickDateButtons setStart={setStart} setEnd={setEnd} />
        </div>
      </div>

      {loading ? <Loader2 className="w-5 h-5 text-amber-400 animate-spin mx-auto mt-8" /> :
      (!data?.records?.length && !data?.rows?.length) ? (
        <div className="text-center py-16 bg-[#1e293b] border border-[#334155] rounded-xl">
          <Banknote className="w-12 h-12 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-400 text-sm">Chưa có dữ liệu bảng kê tiền mặt</p>
          <p className="text-slate-600 text-xs mt-1">Import file Excel hoặc upload ảnh bảng kê</p>
        </div>
      ) : (
        <>
          {/* Charts: Donut (denomination distribution) + Stacked Area (Trezo style) */}
          {aggList.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Donut: By denomination */}
              <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-4" data-testid="cash-donut-chart">
                <p className="text-xs font-semibold text-slate-200 mb-2">Phân bổ theo mệnh giá</p>
                <ApexChart type="donut" height={260}
                  options={{
                    chart: { animations: { enabled: true, speed: 600 } },
                    labels: aggList.map(d => `${fmtFull(d.menh_gia)}đ`),
                    colors: aggList.map(d => MENH_GIA_COLORS[d.menh_gia] || "#64748b"),
                    legend: { position: 'right', fontSize: '9px', labels: { colors: '#94a3b8' },
                      formatter: (name, opts) => `${name} (${aggList[opts.seriesIndex]?.so_to} tờ)`
                    },
                    plotOptions: { pie: { donut: { size: '55%', labels: { show: true,
                      total: { show: true, label: 'Tổng', fontSize: '10px', color: '#94a3b8',
                        formatter: () => fmtVND(grandTotal)
                      },
                      value: { fontSize: '12px', color: '#f8fafc', fontWeight: 700, formatter: v => fmtVND(Number(v)) },
                      name: { fontSize: '9px', color: '#94a3b8' },
                    } } } },
                    dataLabels: { enabled: false },
                    stroke: { show: false },
                    tooltip: { theme: 'dark', y: { formatter: v => fmtFull(v) } },
                  }}
                  series={aggList.map(d => d.thanh_tien)}
                />
              </div>

              {/* Stacked Area Chart (Trezo style) - denominations over days */}
              <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-4" data-testid="cash-area-chart">
                <p className="text-xs font-semibold text-slate-200 mb-2">Chủng loại tiền theo ngày</p>
                {data.records?.length > 0 && (
                  <ApexChart type="area" height={260}
                    options={{
                      chart: { stacked: true, toolbar: { show: false }, animations: { enabled: true, speed: 600 } },
                      stroke: { curve: 'smooth', width: 2 },
                      fill: { type: 'gradient', gradient: { opacityFrom: 0.6, opacityTo: 0.1, stops: [0, 90, 100] } },
                      colors: ['#a78bfa', '#3b82f6', '#06b6d4', '#22c55e', '#eab308', '#f97316', '#ef4444', '#ec4899', '#64748b'],
                      xaxis: {
                        categories: (data.records || []).map(r => `${r.date?.slice(8,10)}/${r.date?.slice(5,7)}`),
                        labels: { style: { fontSize: '9px', colors: '#94a3b8' } },
                        axisBorder: { show: false },
                      },
                      yaxis: { labels: { formatter: v => fmtVND(v), style: { fontSize: '9px', colors: '#94a3b8' } } },
                      grid: { borderColor: '#334155', strokeDashArray: 3, padding: { left: 5 } },
                      legend: { position: 'top', fontSize: '9px', labels: { colors: '#94a3b8' }, horizontalAlign: 'left' },
                      tooltip: { theme: 'dark', y: { formatter: v => fmtFull(v) } },
                      dataLabels: { enabled: false },
                    }}
                    series={[500000, 200000, 100000, 50000, 20000, 10000, 5000, 2000, 1000].map(mg => ({
                      name: `${fmtFull(mg)}đ`,
                      data: (data.records || []).map(rec => {
                        const d = (rec.denominations || []).find(x => x.menh_gia === mg);
                        return d ? d.thanh_tien : 0;
                      }),
                    })).filter(s => s.data.some(v => v > 0))}
                  />
                )}
              </div>
            </div>
          )}

          {/* Detail cards per day */}
          {data.records?.map(rec => (
            <div key={rec.id} className="bg-[#1e293b] border border-[#334155] rounded-xl overflow-hidden">
              {/* Header - matching yellow/orange from image */}
              <div className="text-center py-2.5 border-b border-[#334155]" style={{ background: "linear-gradient(135deg, #78350f 0%, #92400e 50%, #a16207 100%)" }}>
                <p className="text-sm font-black text-white tracking-wide">BẢNG KÊ TIỀN MẶT</p>
                <p className="text-[10px] text-amber-200/80">{rec.date?.slice(8,10)}/{rec.date?.slice(5,7)}/{rec.date?.slice(0,4)}</p>
              </div>
              {/* Column header */}
              <table className="w-full text-xs">
                <thead>
                  <tr style={{ background: "linear-gradient(90deg, #f59e0b, #d97706)" }}>
                    <th className="py-2.5 px-4 text-center text-[#0f172a] font-black w-[60px]">STT</th>
                    <th className="py-2.5 px-4 text-right text-[#0f172a] font-black">MỆNH GIÁ</th>
                    <th className="py-2.5 px-4 text-center text-[#0f172a] font-black">SỐ TỜ</th>
                    <th className="py-2.5 px-4 text-right text-[#0f172a] font-black">THÀNH TIỀN</th>
                  </tr>
                </thead>
                <tbody>
                  {(rec.denominations || []).map((d, i) => {
                    const color = MENH_GIA_COLORS[d.menh_gia] || "#64748b";
                    return (
                    <tr key={i} className={`border-t border-[#334155]/50 ${i % 2 === 0 ? "bg-[#0f172a]" : "bg-[#131d2e]"} hover:bg-[#1e293b] transition-colors`}>
                      <td className="py-2.5 px-4 text-center text-slate-500 font-mono">{i + 1}</td>
                      <td className="py-2.5 px-4 text-right">
                        <span className="inline-flex items-center gap-1.5">
                          <span className="w-2 h-2 rounded-full" style={{ background: color }}></span>
                          <span className="text-slate-200 font-semibold">{fmtFull(d.menh_gia)}</span>
                        </span>
                      </td>
                      <td className="py-2.5 px-4 text-center text-sky-400 font-bold text-sm">{d.so_to}</td>
                      <td className="py-2.5 px-4 text-right text-slate-100 font-medium">{fmtFull(d.thanh_tien)}</td>
                    </tr>
                    );
                  })}
                  {/* Total row */}
                  <tr className="border-t-2 border-amber-500/30" style={{ background: "rgba(245,158,11,0.08)" }}>
                    <td colSpan={3} className="py-3 px-4 text-right font-black text-amber-400 text-xs uppercase">Tổng cộng</td>
                    <td className="py-3 px-4 text-right font-black text-red-400 text-sm">{fmtFull(rec.total)}</td>
                  </tr>
                  <tr className="border-t border-[#334155]/50">
                    <td colSpan={3} className="py-2 px-4 text-right text-sky-300 font-medium italic text-[10px]">Tồn quỹ sổ sách</td>
                    <td className="py-2 px-4 text-right text-sky-400 font-bold">{fmtFull(rec.ton_quy_so_sach)}</td>
                  </tr>
                  <tr className="border-t border-[#334155]/50">
                    <td colSpan={3} className="py-2 px-4 text-right text-red-300 font-medium italic text-[10px]">Chênh lệch</td>
                    <td className={`py-2 px-4 text-right font-bold ${rec.chenh_lech === 0 ? "text-emerald-400" : "text-red-400"}`}>
                      {rec.chenh_lech === 0 ? "-" : fmtFull(rec.chenh_lech)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

// ==================== BẢNG KÊ DOANH THU ====================
function BangKeDoanhthuTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [start, setStart] = useState(() => new Date().toISOString().slice(0, 10));
  const [end, setEnd] = useState(() => new Date().toISOString().slice(0, 10));
  const [view, setView] = useState("detail");
  const [selectedRow, setSelectedRow] = useState(null);
  const [chungtu, setChungtu] = useState(null);
  const [chungtuLoading, setChungtuLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [fullscreenImg, setFullscreenImg] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get(`/accounting/so-thu-chi?start=${start}&end=${end}&view=${view}`); setData(r.data); } catch {}
    finally { setLoading(false); }
  }, [start, end, view]);
  useEffect(() => { fetchData(); }, [fetchData]);

  // Auto-detect empty data → try March 2026
  useEffect(() => {
    if (data && data.rows?.length === 0 && start.includes("2026-04")) {
      setStart("2026-03-01"); setEnd("2026-03-31");
    }
  }, [data, start]);

  // Fetch chungtu when row selected
  useEffect(() => {
    if (!selectedRow?.transaction_id) { setChungtu(null); return; }
    setChungtuLoading(true);
    api.get(`/accounting/chungtu/${selectedRow.transaction_id}`).then(({ data: ct }) => {
      setChungtu(ct);
    }).catch(() => setChungtu(null)).finally(() => setChungtuLoading(false));
  }, [selectedRow]);

  const handleManualUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedRow?.transaction_id) return;
    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async (ev) => {
        const b64 = ev.target.result.split(",")[1];
        await api.post("/accounting/chungtu/manual-attach", {
          transaction_id: selectedRow.transaction_id,
          image_b64: b64,
          label: file.name,
        });
        // Re-fetch to get updated gallery
        const { data: ct } = await api.get(`/accounting/chungtu/${selectedRow.transaction_id}`);
        setChungtu(ct);
        setUploading(false);
      };
      reader.readAsDataURL(file);
    } catch { setUploading(false); }
  };

  const fmtCell = (v) => v ? fmtFull(v) : "";

  return (
    <div className="space-y-3" data-testid="acct-bang-ke-doanh-thu">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-sm font-bold text-slate-200 flex items-center gap-2">
          <LayoutList className="w-4 h-4 text-emerald-400" /> Sổ Theo Dõi Thu Chi
        </h2>
        <div className="flex items-center gap-2">
          <DateRangePicker start={start} end={end} onChangeStart={setStart} onChangeEnd={setEnd} />
          <QuickDateButtons setStart={setStart} setEnd={setEnd} />
        </div>
      </div>

      {/* KPI Summary */}
      {data && data.rows?.length > 0 && (
        <div className="grid grid-cols-4 gap-3">
          <div className="bg-gradient-to-br from-sky-600/20 to-sky-500/5 border border-sky-500/20 rounded-xl p-3">
            <p className="text-[9px] text-sky-300 uppercase font-bold">Tồn đầu kỳ</p>
            <p className="text-sm font-bold text-sky-300">{fmtFull(data.ton_dau_ky || 0)}</p>
          </div>
          <div className="bg-gradient-to-br from-emerald-600/20 to-emerald-500/5 border border-emerald-500/20 rounded-xl p-3">
            <p className="text-[9px] text-emerald-300 uppercase font-bold">Tổng Thu</p>
            <p className="text-sm font-bold text-emerald-400">{fmtFull(data.total_thu || 0)}</p>
          </div>
          <div className="bg-gradient-to-br from-red-600/20 to-red-500/5 border border-red-500/20 rounded-xl p-3">
            <p className="text-[9px] text-red-300 uppercase font-bold">Tổng Chi</p>
            <p className="text-sm font-bold text-red-400">{fmtFull(data.total_chi || 0)}</p>
          </div>
          <div className="bg-gradient-to-br from-amber-600/20 to-amber-500/5 border border-amber-500/20 rounded-xl p-3">
            <p className="text-[9px] text-amber-300 uppercase font-bold">Tồn cuối kỳ</p>
            <p className="text-sm font-bold text-amber-400">{fmtFull(data.ton_cuoi_ky || 0)}</p>
          </div>
        </div>
      )}

      {loading ? <Loader2 className="w-5 h-5 text-emerald-400 animate-spin mx-auto mt-8" /> :
      (!data?.rows?.length) ? (
        <div className="text-center py-16 bg-[#1e293b] border border-[#334155] rounded-xl">
          <LayoutList className="w-10 h-10 text-slate-600 mx-auto mb-2" />
          <p className="text-slate-400 text-sm">Chưa có dữ liệu Sổ Thu Chi trong khoảng thời gian này</p>
        </div>
      ) : (
        <div className="bg-[#1e293b] border border-[#334155] rounded-xl overflow-x-auto">
          {/* Title row */}
          <div className="text-center py-2 border-b border-[#334155]" style={{ background: "linear-gradient(135deg, #0c1524, #0f172a)" }}>
            <p className="text-xs font-bold text-slate-200">SỔ THEO DÕI THU CHI</p>
            <p className="text-[9px] text-slate-500">{start?.slice(8,10)}/{start?.slice(5,7)}/{start?.slice(0,4)} - {end?.slice(8,10)}/{end?.slice(5,7)}/{end?.slice(0,4)}</p>
          </div>
          <table className="w-full text-[10px] min-w-[900px]">
            <thead>
              <tr style={{ background: "#0c1524" }} className="border-b border-[#475569]">
                <th className="py-2.5 px-2 text-center text-slate-400 font-bold w-[40px] border-r border-[#334155]">STT</th>
                <th className="py-2.5 px-2 text-center text-slate-400 font-bold w-[80px] border-r border-[#334155]">Ngày tháng</th>
                <th className="py-2.5 px-2 text-center text-slate-400 font-bold border-r border-[#334155]" colSpan={2}>
                  <div className="flex">
                    <span className="flex-1 text-center text-emerald-400/70">Số THU</span>
                    <span className="flex-1 text-center text-red-400/70">Số CHI</span>
                  </div>
                </th>
                <th className="py-2.5 px-2 text-center text-slate-400 font-bold w-[80px] border-r border-[#334155]">Ngày tháng</th>
                <th className="py-2.5 px-3 text-left text-slate-400 font-bold border-r border-[#334155]">Nội dung</th>
                <th className="py-2.5 px-2 text-center text-slate-400 font-bold border-r border-[#334155]" colSpan={3}>
                  <div className="flex">
                    <span className="flex-1 text-center text-emerald-400">THU</span>
                    <span className="flex-1 text-center text-red-400">CHI</span>
                    <span className="flex-1 text-center text-amber-400">TỒN</span>
                  </div>
                </th>
                <th className="py-2.5 px-2 text-left text-slate-400 font-bold w-[120px] border-r border-[#334155]">Đối tượng</th>
                <th className="py-2.5 px-2 text-left text-slate-400 font-bold w-[80px]">Địa chỉ</th>
              </tr>
            </thead>
            <tbody>
              {/* Opening balance row */}
              {data.ton_dau_ky > 0 && (
                <tr className="bg-sky-500/5 border-b border-[#334155]">
                  <td className="py-2 px-2 text-center text-slate-500 border-r border-[#1e293b]"></td>
                  <td className="py-2 px-2 border-r border-[#1e293b]"></td>
                  <td className="py-2 px-2 border-r border-[#1e293b]" colSpan={2}></td>
                  <td className="py-2 px-2 border-r border-[#1e293b]"></td>
                  <td className="py-2 px-3 text-slate-300 italic border-r border-[#1e293b]">Tồn quỹ cuối ngày trước</td>
                  <td className="py-2 px-2 border-r border-[#1e293b]"></td>
                  <td className="py-2 px-2 border-r border-[#1e293b]"></td>
                  <td className="py-2 px-2 text-right font-bold text-amber-400 border-r border-[#1e293b]">{fmtFull(data.ton_dau_ky)}</td>
                  <td className="py-2 px-2 border-r border-[#1e293b]"></td>
                  <td className="py-2 px-2"></td>
                </tr>
              )}
              {data.rows.map((r, i) => (
                r.is_total ? (
                  <tr key={i} className="border-t-2 border-[#475569]" style={{ background: "rgba(99,102,241,0.06)" }}>
                    <td colSpan={5} className="py-2 px-3 border-r border-[#334155]"></td>
                    <td className="py-2 px-3 font-bold text-indigo-300 border-r border-[#334155]">{r.description}</td>
                    <td className="py-2 px-2 text-right font-bold text-emerald-400 border-r border-[#1e293b]">{r.thu > 0 ? fmtFull(r.thu) : ""}</td>
                    <td className="py-2 px-2 text-right font-bold text-red-400 border-r border-[#1e293b]">{r.chi > 0 ? fmtFull(r.chi) : ""}</td>
                    <td className="py-2 px-2 text-right font-bold text-amber-400 border-r border-[#1e293b]">{fmtFull(r.ton)}</td>
                    <td className="py-2 px-2 border-r border-[#1e293b]"></td>
                    <td className="py-2 px-2"></td>
                  </tr>
                ) : (
                  <tr key={i} onClick={() => setSelectedRow(r)} data-testid={`bkdt-row-${i}`}
                    className={`border-t border-[#1e293b] cursor-pointer hover:bg-indigo-500/5 transition-colors ${i % 2 === 0 ? "bg-[#0f172a]" : "bg-[#131d2e]"}`}>
                    <td className="py-2 px-2 text-center text-slate-500 font-mono text-[9px] border-r border-[#1e293b]">{r.stt}</td>
                    <td className="py-2 px-2 text-center text-slate-400 font-mono text-[9px] border-r border-[#1e293b]">{r.date?.slice(8,10)}/{r.date?.slice(5,7)}/{r.date?.slice(0,4)}</td>
                    <td className="py-2 px-2 text-center text-emerald-400 text-[9px] font-medium">{r.chung_tu_thu || ""}</td>
                    <td className="py-2 px-2 text-center text-red-400 text-[9px] font-medium border-r border-[#1e293b]">{r.chung_tu_chi || ""}</td>
                    <td className="py-2 px-2 text-center text-slate-500 font-mono text-[9px] border-r border-[#1e293b]">{r.date?.slice(8,10)}/{r.date?.slice(5,7)}/{r.date?.slice(0,4)}</td>
                    <td className="py-2 px-3 text-slate-200 truncate max-w-[250px] border-r border-[#1e293b]" title={r.noi_dung}>{r.noi_dung}</td>
                    <td className="py-2 px-2 text-right text-emerald-400 font-medium border-r border-[#1e293b]">{r.thu > 0 ? fmtCell(r.thu) : ""}</td>
                    <td className="py-2 px-2 text-right text-red-400 font-medium border-r border-[#1e293b]">{r.chi > 0 ? fmtCell(r.chi) : ""}</td>
                    <td className="py-2 px-2 text-right text-amber-400 font-bold border-r border-[#1e293b]">{fmtCell(r.ton)}</td>
                    <td className="py-2 px-2 text-slate-300 text-[9px] truncate max-w-[100px] border-r border-[#1e293b]" title={r.doi_tuong}>{r.doi_tuong}</td>
                    <td className="py-2 px-2 text-slate-500 text-[9px]">{r.dia_chi}</td>
                  </tr>
                )
              ))}
              {/* Grand total */}
              <tr className="border-t-2 border-lime-500/30" style={{ background: "linear-gradient(90deg, #1a2332, #0f172a)" }}>
                <td colSpan={5} className="py-2.5 px-3 border-r border-[#334155]"></td>
                <td className="py-2.5 px-3 font-bold text-slate-100 border-r border-[#334155]">Tổng cộng</td>
                <td className="py-2.5 px-2 text-right font-bold text-emerald-400 text-[11px] border-r border-[#1e293b]">{fmtFull(data.total_thu || 0)}</td>
                <td className="py-2.5 px-2 text-right font-bold text-red-400 text-[11px] border-r border-[#1e293b]">{fmtFull(data.total_chi || 0)}</td>
                <td className="py-2.5 px-2 text-right font-bold text-amber-400 text-[11px] border-r border-[#1e293b]">{fmtFull(data.ton_cuoi_ky || 0)}</td>
                <td className="py-2.5 px-2 border-r border-[#1e293b]"></td>
                <td className="py-2.5 px-2"></td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* Detail popup */}
      {selectedRow && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => setSelectedRow(null)} data-testid="bkdt-detail-popup">
          <div onClick={e => e.stopPropagation()} className="bg-[#1e293b] border border-[#475569] rounded-2xl shadow-2xl p-6 w-full max-w-lg mx-4 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-bold text-slate-100">Chi tiết giao dịch #{selectedRow.stt}</h3>
              <button onClick={() => setSelectedRow(null)} className="text-slate-500 hover:text-slate-200 text-lg">&times;</button>
            </div>
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div className="bg-[#0f172a] rounded-lg p-3">
                <p className="text-slate-500 text-[9px] uppercase mb-1">Ngày</p>
                <p className="text-slate-200 font-medium">{selectedRow.date?.slice(8,10)}/{selectedRow.date?.slice(5,7)}/{selectedRow.date?.slice(0,4)}</p>
              </div>
              <div className="bg-[#0f172a] rounded-lg p-3">
                <p className="text-slate-500 text-[9px] uppercase mb-1">Số chứng từ</p>
                <p className={`font-bold ${selectedRow.chung_tu_thu ? "text-emerald-400" : "text-red-400"}`}>{selectedRow.chung_tu_thu || selectedRow.chung_tu_chi || "-"}</p>
              </div>
              <div className="col-span-2 bg-[#0f172a] rounded-lg p-3">
                <p className="text-slate-500 text-[9px] uppercase mb-1">Nội dung</p>
                <p className="text-slate-200">{selectedRow.noi_dung}</p>
              </div>
              <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-3">
                <p className="text-emerald-400 text-[9px] uppercase mb-1">Số tiền THU</p>
                <p className="text-emerald-400 font-bold text-lg">{selectedRow.thu > 0 ? fmtFull(selectedRow.thu) : "-"}</p>
              </div>
              <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
                <p className="text-red-400 text-[9px] uppercase mb-1">Số tiền CHI</p>
                <p className="text-red-400 font-bold text-lg">{selectedRow.chi > 0 ? fmtFull(selectedRow.chi) : "-"}</p>
              </div>
              <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3">
                <p className="text-amber-400 text-[9px] uppercase mb-1">Tồn quỹ</p>
                <p className="text-amber-400 font-bold text-lg">{fmtFull(selectedRow.ton)}</p>
              </div>
              <div className="bg-[#0f172a] rounded-lg p-3">
                <p className="text-slate-500 text-[9px] uppercase mb-1">Loại</p>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${selectedRow.thu > 0 ? "bg-emerald-500/20 text-emerald-400" : "bg-red-500/20 text-red-400"}`}>
                  {selectedRow.thu > 0 ? "Phiếu thu" : "Phiếu chi"}
                </span>
              </div>
              <div className="bg-[#0f172a] rounded-lg p-3">
                <p className="text-slate-500 text-[9px] uppercase mb-1">Đối tượng</p>
                <p className="text-slate-200 font-medium">{selectedRow.doi_tuong || "-"}</p>
              </div>
              <div className="bg-[#0f172a] rounded-lg p-3">
                <p className="text-slate-500 text-[9px] uppercase mb-1">Địa chỉ / Chức vụ</p>
                <p className="text-slate-200">{selectedRow.dia_chi || "-"}</p>
              </div>
            </div>

            {/* Chứng từ image section - GALLERY MODE */}
            <div className="border-t border-[#334155] pt-4 mt-2">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[10px] text-slate-400 uppercase font-bold">
                  Hình ảnh chứng từ {chungtu?.total > 0 && `(${chungtu.total})`}
                </p>
                <label className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[9px] font-medium cursor-pointer transition-colors ${uploading ? "bg-slate-700 text-slate-500" : "bg-sky-500/15 text-sky-400 hover:bg-sky-500/25 border border-sky-500/30"}`}>
                  {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <svg className="w-3 h-3" viewBox="0 0 16 16" fill="none"><path d="M8 2V11M4 6L8 2L12 6M2 14H14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                  {uploading ? "Đang tải..." : "Thêm ảnh"}
                  <input type="file" accept="image/*,.pdf" className="hidden" onChange={handleManualUpload} disabled={uploading} />
                </label>
              </div>

              {chungtuLoading ? (
                <div className="flex items-center justify-center py-6"><Loader2 className="w-4 h-4 text-slate-500 animate-spin" /></div>
              ) : chungtu?.images?.length > 0 ? (
                <div className="grid grid-cols-3 gap-2">
                  {chungtu.images.map((img, idx) => (
                    <div key={idx} className="relative group cursor-pointer" onClick={() => setFullscreenImg(img)}>
                      <img src={`data:image/jpeg;base64,${img.image_b64}`} alt={img.label}
                        className="w-full h-20 object-cover rounded-lg border border-[#334155] hover:border-violet-500/50 transition-colors" />
                      <span className={`absolute top-1 right-1 px-1 py-0.5 rounded text-[7px] font-bold ${img.matched_by === "ai" || img.matched_by === "ai_created" ? "bg-violet-500/80 text-white" : "bg-sky-500/80 text-white"}`}>
                        {img.matched_by === "ai" || img.matched_by === "ai_created" ? "AI" : "Tay"}
                      </span>
                      <p className="text-[8px] text-slate-500 mt-0.5 truncate">{img.label}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="border-2 border-dashed border-[#334155] rounded-lg p-4 text-center">
                  <p className="text-slate-600 text-xs">Chưa có chứng từ đính kèm</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Fullscreen image viewer */}
      {fullscreenImg && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/90 backdrop-blur-sm" onClick={() => setFullscreenImg(null)} data-testid="fullscreen-viewer">
          <div className="relative max-w-[90vw] max-h-[90vh]" onClick={e => e.stopPropagation()}>
            <button onClick={() => setFullscreenImg(null)} className="absolute -top-3 -right-3 w-8 h-8 bg-[#1e293b] border border-[#475569] rounded-full flex items-center justify-center text-slate-300 hover:text-white z-10">&times;</button>
            <img src={`data:image/jpeg;base64,${fullscreenImg.image_b64}`} alt={fullscreenImg.label}
              className="max-w-[90vw] max-h-[85vh] object-contain rounded-xl shadow-2xl" />
            <div className="mt-2 flex items-center justify-between">
              <p className="text-xs text-slate-400">{fullscreenImg.label}</p>
              <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold ${fullscreenImg.matched_by === "ai" || fullscreenImg.matched_by === "ai_created" ? "bg-violet-500/20 text-violet-400" : "bg-sky-500/20 text-sky-400"}`}>
                {fullscreenImg.matched_by === "ai" || fullscreenImg.matched_by === "ai_created" ? "AI tự động" : "Gắn tay"}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ==================== KIOTVIET vs OCR COMPARISON ====================
function KVComparisonSection({ start, end }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const days = Math.max(1, Math.ceil((new Date(end) - new Date(start)) / 86400000));
    api.get(`/kiotviet/comparison?days=${days}`).then(r => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, [start, end]);

  if (loading || !data || !data.chart_data?.some(d => d.kiotviet > 0 || d.accounting > 0)) return null;

  return (
    <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-4 space-y-3" data-testid="kv-ocr-comparison">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold text-slate-200">So sánh: Ghi sổ (OCR) vs KiotViet</p>
        {data.alerts?.length > 0 && (
          <Badge className="text-[8px] bg-amber-500/20 text-amber-400 border-0">{data.alerts.length} chênh lệch</Badge>
        )}
      </div>

      {/* Totals comparison */}
      <div className="grid grid-cols-3 gap-2">
        <div className="bg-[#0f172a] rounded-lg p-3 text-center border border-[#334155]/50">
          <p className="text-[8px] text-slate-600 uppercase">Ghi sổ (OCR)</p>
          <p className="text-lg font-bold text-amber-400">{fmtVND(data.totals?.accounting || 0)}</p>
        </div>
        <div className="bg-[#0f172a] rounded-lg p-3 text-center border border-[#334155]/50">
          <p className="text-[8px] text-slate-600 uppercase">KiotViet</p>
          <p className="text-lg font-bold text-[#4680ff]">{fmtVND(data.totals?.kiotviet || 0)}</p>
        </div>
        <div className="bg-[#0f172a] rounded-lg p-3 text-center border border-[#334155]/50">
          <p className="text-[8px] text-slate-600 uppercase">Chênh lệch</p>
          <p className={`text-lg font-bold ${Math.abs((data.totals?.kiotviet || 0) - (data.totals?.accounting || 0)) > 0 ? "text-red-400" : "text-emerald-400"}`}>
            {fmtVND(Math.abs((data.totals?.kiotviet || 0) - (data.totals?.accounting || 0)))}
          </p>
        </div>
      </div>

      {/* Comparison chart */}
      {(
        <ApexChart type="bar" height={180} options={{
          chart: { toolbar: { show: false }, animations: { enabled: true, speed: 600 } },
          plotOptions: { bar: { borderRadius: 3, columnWidth: '60%' } },
          colors: ['#fbbc0b', '#4680ff'],
          xaxis: { categories: data.chart_data.filter(d => d.kiotviet > 0 || d.accounting > 0).map(d => d.date?.slice(5) || ""), labels: { style: { fontSize: '9px' } } },
          yaxis: { labels: { formatter: v => fmtVND(v), style: { fontSize: '9px' } } },
          grid: { borderColor: '#334155', strokeDashArray: 3 },
          legend: { position: 'bottom', fontSize: '10px' },
          tooltip: { y: { formatter: v => fmtFull(v) } },
          dataLabels: { enabled: false },
        }} series={[
          { name: 'Ghi sổ (OCR)', data: data.chart_data.filter(d => d.kiotviet > 0 || d.accounting > 0).map(d => d.accounting) },
          { name: 'KiotViet', data: data.chart_data.filter(d => d.kiotviet > 0 || d.accounting > 0).map(d => d.kiotviet) },
        ]} />
      )}

      {/* Daily detail table with discrepancies highlighted */}
      {data.alerts?.length > 0 && (
        <div>
          <p className="text-[10px] text-red-400 mb-1.5">Ngày có chênh lệch:</p>
          <div className="space-y-1 max-h-32 overflow-y-auto">
            {data.alerts.map((a, i) => (
              <div key={i} className="flex items-center justify-between py-1 px-2 rounded bg-red-500/5 border border-red-500/10">
                <span className="text-[10px] text-slate-400">{a.date}</span>
                <div className="flex gap-3 text-[10px]">
                  <span className="text-amber-400">OCR: {fmtFull(a.accounting)}</span>
                  <span className="text-[#4680ff]">KV: {fmtFull(a.kiotviet)}</span>
                  <span className="text-red-400 font-medium">±{a.diff_pct}%</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
